import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    styled,
    alpha,
    Divider,
    FormControlLabel,
    Checkbox,
    useTheme,
    IconButton,
} from '@mui/material';
import {
    Lock,
    Email,
    Visibility,
    VisibilityOff,
    Person,
    Business,
    Phone,
    ArrowBack as ArrowBackIcon,
    WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SignUpRequest, validateEmail, validateOrganizationName, saveTemporarySignupData, getLogoByDomain } from '../utils/api';
import { formatPhoneNumber, cleanPhoneNumber, phoneMaskProps } from '../utils/phoneMask';

const BackgroundContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: theme.palette.mode === 'dark'
        ? '#313131'
        : `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)
    `,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: 24,
    boxShadow: theme.palette.mode === 'dark' ? '0 20px 60px rgba(0, 0, 0, 0.4)' : '0 20px 60px rgba(0, 0, 0, 0.08)',
    backdropFilter: 'blur(20px)',
    background: theme.palette.mode === 'dark' ? '#1a1a1a' : 'rgba(255, 255, 255, 0.95)',
    border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(255, 255, 255, 0.3)',
    maxWidth: 500,
    width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 12,
        backgroundColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.02),
        transition: 'all 0.3s ease',

        '& fieldset': {
            borderColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.1),
            borderWidth: 2,
        },

        '&:hover fieldset': {
            borderColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.3),
        },

        '&.Mui-focused': {
            backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#FFFFFF',

            '& fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
        },
    },

    '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,

        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },

    '& .MuiInputAdornment-root': {
        color: theme.palette.mode === 'dark' ? alpha('#E5E7EB', 0.7) : alpha(theme.palette.primary.main, 0.6),
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.5),
    fontSize: '1rem',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(4),

    '& img': {
        transition: 'transform 0.3s ease',

        '&:hover': {
            transform: 'scale(1.05)',
        },
    },
}));

const LogoImage = styled('img')(({ theme }) => ({
    width: '240px',
    height: 'auto',
    filter: theme.palette.mode === 'dark'
        ? 'invert(1) brightness(1.2) drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
        : 'brightness(0.2) drop-shadow(0 4px 12px rgba(0,0,0,0.1))',
}));

const WhatsAppButton = styled(IconButton)(({ theme }) => ({
    position: 'fixed',
    bottom: 100, // Mais alto no mobile para não sobrepor o botão continuar
    right: 16,
    width: 56,
    height: 56,
    backgroundColor: '#25D366',
    color: 'white',
    zIndex: 1000,
    boxShadow: '0 8px 25px rgba(37, 211, 102, 0.4)',
    transition: 'all 0.3s ease',
    [theme.breakpoints.up('sm')]: {
        bottom: 24,
        right: 24,
        width: 64,
        height: 64,
    },
    '&:hover': {
        backgroundColor: '#20BA5C',
        transform: 'scale(1.1)',
        boxShadow: '0 12px 35px rgba(37, 211, 102, 0.5)',
    },
}));

interface SignUpFormProps {
    onSignUpComplete: (data: SignUpRequest) => Promise<void>;
    onBack: () => void;
    initialData?: SignUpRequest;
    isLoading?: boolean;
}

// Função para gerar um ID único
const generateUniqueId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${Math.random().toString(36).substr(2, 9)}`;
};

const SignUpForm: React.FC<SignUpFormProps> = ({ onSignUpComplete, onBack, initialData, isLoading = false }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const sessionIdRef = useRef<string | null>(null);
    const [formData, setFormData] = useState<SignUpRequest>(initialData || {
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        organizationName: '',
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState<{
        email?: string;
        organizationName?: string;
    }>({});
    const [validating, setValidating] = useState<{
        email: boolean;
        organizationName: boolean;
    }>({ email: false, organizationName: false });
    const [dynamicLogo, setDynamicLogo] = useState<string | null>(null);
    const [logoLoading, setLogoLoading] = useState(false);

    // Gerar ID único quando o componente montar
    useEffect(() => {
        // Verificar se já existe um ID na sessão
        const existingSessionId = sessionStorage.getItem('signupSessionId');
        if (existingSessionId) {
            sessionIdRef.current = existingSessionId;
        } else {
            // Gerar novo ID único
            const newSessionId = generateUniqueId();
            sessionStorage.setItem('signupSessionId', newSessionId);
            sessionIdRef.current = newSessionId;
        }
    }, []);

    React.useEffect(() => {
        if (initialData) {
            setFormData(initialData);
        }
    }, [initialData]);

    // Função para extrair domínio da URL atual
    const getCurrentDomain = () => {
        const currentUrl = window.location.hostname;
        return currentUrl;
    };

    const isAllowedDomain = () => {
        const currentDomain = window.location.origin;
        return currentDomain === 'http://localhost:3000' || currentDomain === 'https://platform.liatalk.ai';
    };

    // Carregar logo dinamicamente baseado no domínio
    React.useEffect(() => {
        const loadDynamicLogo = async () => {
            const domain = getCurrentDomain();

            // Só tenta carregar logo se não for localhost:3000 ou platform.liatalk.ai
            if (domain !== 'localhost' && domain !== 'platform.liatalk.ai') {
                setLogoLoading(true);
                try {
                    const logoData = await getLogoByDomain(domain);
                    if (logoData?.logoUrl) {
                        setDynamicLogo(logoData.logoUrl);
                    }
                } catch (error) {
                    console.error('Error loading dynamic logo:', error);
                } finally {
                    setLogoLoading(false);
                }
            }
        };

        loadDynamicLogo();
    }, []);

    const handleWhatsAppClick = () => {
        const phoneNumber = '5511995107703';
        const whatsappUrl = `https://wa.me/${phoneNumber}`;
        window.open(whatsappUrl, '_blank');
    };

    // Função para salvar dados temporários no backend
    const saveTemporaryData = async (field: 'name' | 'email' | 'phone', value: string) => {
        if (!sessionIdRef.current || !value.trim()) {
            return;
        }

        try {
            // Enviar todos os campos preenchidos para garantir que o backend tenha dados completos
            const dataToSave: { sessionId: string; name?: string; email?: string; phone?: string } = {
                sessionId: sessionIdRef.current,
            };

            // Adicionar todos os campos que já foram preenchidos
            if (formData.name.trim()) {
                dataToSave.name = formData.name.trim();
            }
            if (formData.email.trim()) {
                dataToSave.email = formData.email.trim();
            }
            if (formData.phone.trim()) {
                dataToSave.phone = cleanPhoneNumber(formData.phone);
            }

            // Atualizar o campo que acabou de ser modificado
            if (field === 'phone') {
                dataToSave.phone = cleanPhoneNumber(value);
            } else {
                dataToSave[field] = value.trim();
            }

            await saveTemporarySignupData(dataToSave);
        } catch (error) {
            // Silenciosamente falhar - não queremos interromper o fluxo do usuário
            console.error('Erro ao salvar dados temporários:', error);
        }
    };

    const handleInputChange = (field: keyof SignUpRequest, value: string) => {
        if (field === 'phone') {
            const formatted = formatPhoneNumber(value);
            setFormData(prev => ({ ...prev, [field]: formatted }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));

            // Validar email em tempo real
            if (field === 'email' && value.trim()) {
                validateEmailField(value.trim());
            }

            // Validar nome da empresa em tempo real
            if (field === 'organizationName' && value.trim()) {
                validateOrganizationField(value.trim());
            }
        }
    };

    // Handlers para onBlur - salvar dados quando o usuário sair do campo
    const handleNameBlur = () => {
        if (formData.name.trim()) {
            saveTemporaryData('name', formData.name);
        }
    };

    const handleEmailBlur = () => {
        if (formData.email.trim()) {
            saveTemporaryData('email', formData.email);
        }
    };

    const handlePhoneBlur = () => {
        if (formData.phone.trim()) {
            saveTemporaryData('phone', formData.phone);
        }
    };

    const validateEmailField = async (email: string) => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setValidationErrors(prev => ({ ...prev, email: undefined }));
            return;
        }

        setValidating(prev => ({ ...prev, email: true }));
        try {
            const result = await validateEmail(email);
            if (result.exists) {
                setValidationErrors(prev => ({
                    ...prev,
                    email: result.message || 'Este email já está em uso'
                }));
            } else {
                setValidationErrors(prev => ({ ...prev, email: undefined }));
            }
        } catch (error) {
            console.error('Erro ao validar email:', error);
        } finally {
            setValidating(prev => ({ ...prev, email: false }));
        }
    };

    const validateOrganizationField = async (organizationName: string) => {
        if (!organizationName.trim()) {
            setValidationErrors(prev => ({ ...prev, organizationName: undefined }));
            return;
        }

        setValidating(prev => ({ ...prev, organizationName: true }));
        try {
            const result = await validateOrganizationName(organizationName);
            if (result.exists) {
                setValidationErrors(prev => ({
                    ...prev,
                    organizationName: result.message || 'Este nome de empresa já está em uso'
                }));
            } else {
                setValidationErrors(prev => ({ ...prev, organizationName: undefined }));
            }
        } catch (error) {
            console.error('Erro ao validar nome da empresa:', error);
        } finally {
            setValidating(prev => ({ ...prev, organizationName: false }));
        }
    };

    const handleTermsClick = () => {
        navigate('/termos-de-uso', {
            state: {
                fromSignUp: true,
                formData: formData
            }
        });
    };

    const handlePrivacyClick = () => {
        navigate('/politica-de-privacidade', {
            state: {
                fromSignUp: true,
                formData: formData
            }
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError('As senhas não coincidem');
            setLoading(false);
            return;
        }

        if (!acceptTerms) {
            setError('Você deve aceitar os termos de uso');
            setLoading(false);
            return;
        }

        if (formData.password.length < 6) {
            setError('A senha deve ter pelo menos 6 caracteres');
            setLoading(false);
            return;
        }

        // Verificar se há erros de validação
        if (validationErrors.email || validationErrors.organizationName) {
            setError('Por favor, corrija os erros antes de continuar');
            setLoading(false);
            return;
        }

        // Verificar se ainda está validando
        if (validating.email || validating.organizationName) {
            setError('Aguarde a validação dos dados');
            setLoading(false);
            return;
        }

        try {
            await onSignUpComplete(formData);
        } catch (err: any) {
            setError(err.message || 'Erro ao processar dados');
            setLoading(false);
        }
    };

    return (
        <BackgroundContainer>
            <Container maxWidth="sm">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {(isAllowedDomain() || dynamicLogo) && (
                        <LogoContainer>
                            <Box sx={{ mb: 0 }}>
                                {logoLoading ? (
                                    <CircularProgress size={40} />
                                ) : (
                                    <LogoImage
                                        src={dynamicLogo || "/logo-preto.svg"}
                                        alt="Logo"
                                        onError={() => setDynamicLogo(null)} // Fallback para logo padrão em caso de erro
                                    />
                                )}
                            </Box>
                        </LogoContainer>
                    )}

                    <StyledPaper elevation={3}>
                        <Box mb={3}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={onBack}
                                sx={{ mb: 2 }}
                            >
                                Voltar
                            </Button>
                            {/* <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" textAlign="center">
                                Criar Conta
                            </Typography> */}
                            <Typography variant="body1" color="text.secondary" textAlign="center">
                                Preencha os dados abaixo para começar
                            </Typography>
                        </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Box mb={2}>
                            <StyledTextField
                                fullWidth
                                label="Nome Completo"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                onBlur={handleNameBlur}
                                required
                                InputProps={{
                                    startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                                }}
                            />
                        </Box>

                        <Box mb={2}>
                            <StyledTextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                onBlur={handleEmailBlur}
                                required
                                error={!!validationErrors.email}
                                helperText={validationErrors.email}
                                InputProps={{
                                    startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                                    endAdornment: validating.email ? (
                                        <CircularProgress size={20} />
                                    ) : null,
                                }}
                            />
                        </Box>

                        <Box mb={2}>
                            <StyledTextField
                                fullWidth
                                label="Telefone"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                onBlur={handlePhoneBlur}
                                required
                                InputProps={{
                                    startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                                    ...phoneMaskProps,
                                }}
                            />
                        </Box>

                        <Box mb={2}>
                            <StyledTextField
                                fullWidth
                                label="Nome da Empresa"
                                value={formData.organizationName}
                                onChange={(e) => handleInputChange('organizationName', e.target.value)}
                                required
                                error={!!validationErrors.organizationName}
                                helperText={validationErrors.organizationName}
                                InputProps={{
                                    startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />,
                                    endAdornment: validating.organizationName ? (
                                        <CircularProgress size={20} />
                                    ) : null,
                                }}
                            />
                        </Box>

                        <Box mb={2}>
                            <StyledTextField
                                fullWidth
                                label="Senha"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                required
                                InputProps={{
                                    startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                                    endAdornment: (
                                        <Button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            sx={{ minWidth: 'auto', p: 1 }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </Button>
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                fullWidth
                                label="Confirmar Senha"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                required
                                InputProps={{
                                    startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                                    endAdornment: (
                                        <Button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            sx={{ minWidth: 'auto', p: 1 }}
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </Button>
                                    ),
                                }}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={acceptTerms}
                                    onChange={(e) => setAcceptTerms(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" color="text.secondary">
                                    Eu aceito os{' '}
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={handleTermsClick}
                                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                    >
                                        Termos de Uso
                                    </Button>{' '}
                                    e{' '}
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={handlePrivacyClick}
                                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                    >
                                        Política de Privacidade
                                    </Button>
                                </Typography>
                            }
                            sx={{ mb: 3 }}
                        />

                        {(loading || isLoading) ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    py: 2,
                                }}
                            >
                                <CircularProgress size={24} />
                                <Typography variant="body1" color="text.secondary">
                                    Criando a conta...
                                </Typography>
                            </Box>
                        ) : (
                            <StyledButton
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                                disabled={loading || !!validationErrors.email || !!validationErrors.organizationName || validating.email || validating.organizationName}
                                size="large"
                            >
                                Continuar
                            </StyledButton>
                        )}
                    </form>
                    </StyledPaper>
                </Box>
            </Container>

            {/* Botão flutuante do WhatsApp */}
            <WhatsAppButton onClick={handleWhatsAppClick} aria-label="WhatsApp">
                <WhatsAppIcon sx={{ fontSize: 32 }} />
            </WhatsAppButton>
        </BackgroundContainer>
    );
};

export default SignUpForm;
